import { config } from '@shopee-user/platform';
import { Env } from '@shopee-user/global';
import { FeatureToggleConfig } from './types';

export enum Feature {
  /** TobWebTrackingSdkV4Integration
   * Controls whether the SDK V4 is used to send TMS Tracking
   * FE TD: https://confluence.shopee.io/display/SAUT/%5BToB%5D%5BWeb%5D%5BTD%5D+Upgrade+FE+Data+Tracker+to+V4
   */
  TobWebTrackingSdkV4Integration = 'TobWebTrackingSdkV4Integration',
  /**
   * StandaloneCaptchaV2Upgrade
   * Controls whether `@shopee/captcha-components-mobile-standalone` latest version should be used
   * Created for SPX Agency project, to use Captcha theme feature
   */
  StandaloneCaptchaV2Upgrade = 'StandaloneCaptchaV2Upgrade',
  StandaloneCaptchaEagerLoad = 'StandaloneCaptchaEagerLoad',
  /**
   * Controls whether `@shopee/captcha-base` should be used
   */
  StandaloneCaptchaV3Upgrade = 'StandaloneCaptchaV3Upgrade',
  /**
   * Toggle for Audio Captcha in @shopee/captcha-base@1.0.0
   */
  UseAudioCaptcha = 'UseAudioCaptcha',
  /**
   * Toggle to enable biometrics-related features
   * https://jira.shopee.io/browse/SPB-16994
   */
  TobBiometricsIntegration = 'TobBiometricsIntegration',
  // for feature using Passcode
  TobBiometricsIntegrationV2 = 'TobBiometricsIntegrationV2',
  /**
   * HideBiometricsSetUpLaterButtonInAuthentication
   * Controls whether to force biometrics activation for Shopee Food Driver
   */
  HideBiometricsSetUpLaterButtonInAuthentication = 'HideBiometricsSetUpLaterButtonInAuthentication',
  /**
   * AllowResetPasswordJumpToToC
   * Controls whether jump to ToC reset password flow in webview is enabled.
   */
  AllowResetPasswordJumpToToC = 'AllowResetPasswordJumpToToC',

  // APMS Web JS error monitoring
  EnableAPMSWebJsErrorMonitoring = 'enable_apms_web_js_error_monitoring',
  // MDAP performance monitoring
  EnableMDAPPerformanceMonitoring = 'enable_mdap_performance_monitoring',

  // Layout Restructure Feature Toggle
  SignupByOtpOneCredentialLayoutRefactor = 'signup_by_otp_one_credential_layout_refactor',

  // Add/Change Phone/Email for One Credential Credential Changes
  AddChangeToCPhoneNumber = 'AddChangeToCPhoneNumber',
  AddChangeToCEmail = 'AddChangeToCEmail',

  LoginByOtpOneCredentialLayoutRefactor = 'login_by_otp_one_credential_layout_refactor',

  // Thirdparty Integration
  EnableThirdpartyIntegrationSupportShopeeInApp = 'EnableThirdpartyIntegrationSupportShopeeInApp',
  EnableThirdpartyIntegrationSupportShopeeInWeb = 'EnableThirdpartyIntegrationSupportShopeeInWeb',
  EnableThirdpartyIntegrationSupportGoogleInApp = 'EnableThirdpartyIntegrationSupportGoogleInApp',
  EnableThirdpartyIntegrationSupportGoogleInWeb = 'EnableThirdpartyIntegrationSupportGoogleInWeb',
  EnableThirdpartyIntegrationSupportFacebookInApp = 'EnableThirdpartyIntegrationSupportFacebookInApp',
  EnableThirdpartyIntegrationSupportFacebookInWeb = 'EnableThirdpartyIntegrationSupportFacebookInWeb',
  EnableThirdpartyIntegrationSupportAppleInApp = 'EnableThirdpartyIntegrationSupportAppleInApp',
  EnableThirdpartyIntegrationSupportAppleInWeb = 'EnableThirdpartyIntegrationSupportAppleInWeb',

  // Support WAL
  SupportWAL = 'SupportWal',
  // Warehouse Management System Support KR Region
  WarehouseManagementSystemSupportKRRegion = 'warehouse_management_system_support_kr_region',

  // TD: https://confluence.shopee.io/x/AEbbow
  // This toggle will control whether ToB login APIs use native security device fingerprint.
  // Please align with Antifraud Team (IVS team) before toggle ON or toggle OFF this toggle.
  // It will impact ToB login IVS.
  // It should align with IvsIntegration toggle.
  DisableNativeDeviceFingerPrint = 'disable_native_device_finger_print',
  /**
   * LoginUseProxyDomain
   * Controls whether redirects between SPX main/staff use shared accounts domain with path proxies
   * True: use https://accounts.<env>.spx.<cid>/(main|staff) + route (e.g. /authenticate/login)
   * False: use https://spx(-staff).business.accounts.<env>.shopee.<cid> + route
   */
  LoginUseProxyDomain = 'LoginUseProxyDomain',
  /**
   * SPXMainStaffLogin
   * Controls whether to show the main staff login link in login page
   * TODO: remove this toggle after rollout
   */
  SPXMainStaffLogin = 'SPXMainStaffLogin',

  /**
   * TD: https://confluence.shopee.io/pages/viewpage.action?pageId=2992580767
   * when toggle on - we will use PKCE params to Facebook
   * when toggle off - we will not use PKCE params to Facebook
   */
  EnablePKCEParams = 'enable_pkce_params',
  EnableThirdPartyServiceBindingInAuthenticationFlow = 'EnableThirdPartyServiceBinding',
  /**
   * OtpSessionSupport
   * Controls whether to support OTP session
   * Implemented in ToB web OTP shared service.
   * When true, OTP session is supported
   * When false, OTP session is not supported
   * Reference: https://confluence.shopee.io/pages/viewpage.action?pageId=3065956249
   */
  OtpSessionSupport = 'OtpSessionSupport',
  // Migration to Static Asset Redirect Client ID Whitelist
  MigrateRedirectUrlConfigurationToStaticAsset = 'MigrateRedirectUrlConfigurationToStaticAsset',
  /**
   * TD: https://confluence.shopee.io/display/SAUT/FE+TD+%7C+Initialising+SPX+Agency+Ops+Application
   * When toggle on: Reset Password Page is accessible
   * When toggle off: Reset Password Page is not accessible
   */
  ResetPasswordEntrance = 'ResetPasswordEntrance',
  ShouldEnableQuickLoginDefaultValue = 'ShouldEnableQuickLoginDefaultValue',
}

let localStaticToggles = undefined;

/**
 * This block will be removed by webpack in production mode
 */
if (process.env.NODE_ENV === 'development' || process.env.E2E) {
  const isLive = config.ENV === Env.Live || config.ENV === Env.Liveish;
  const businessTarget = config.BUSINESS_TARGET;

  localStaticToggles = require('../local-static-toggles.json')[businessTarget][
    isLive ? 'live' : 'non-live'
  ];
}

export const UserFeatureToggleConfig: FeatureToggleConfig<Feature> =
  (self as any).__ASSETS__?.USER_STATIC_TOGGLE || localStaticToggles || {};
